<!--
	Wrapper component: renderless. The script owns state and requests; the template only
	exposes them through a scoped slot and renders no markup of its own. Twig owns every
	line of markup inside <dealer-reply …> and binds to the slot props with v-slot="{ … }",
	${ }, v-model, :disabled, and @click. Inputs arrive as kebab-case attributes on the tag.
-->
<template>
	<slot v-bind="{ editing, saving, error, text, edit, cancel, save, remove }" />
</template>

<script setup>
import { ref } from "vue";
import { postAction } from "@composables/api";

const props = defineProps({
	reviewId: {
		type: Number,
		required: true,
	},
	// The existing Reply's id: 0 means the Review has no Reply yet
	replyId: {
		type: Number,
		default: 0,
	},
	initialText: {
		type: String,
		default: "",
	},
	initialEditing: {
		type: Boolean,
		default: false,
	},
});

const editing = ref(props.initialEditing);
const saving = ref(false);
const error = ref("");
const text = ref(props.initialText);

function edit() {
	text.value = props.initialText;
	error.value = "";
	editing.value = true;
}

function cancel() {
	editing.value = false;
	text.value = props.initialText;
	error.value = "";
}

// Post to an action, then reload on success so the list, count, and pagination come from the server
async function run(action, payload, fallback) {
	saving.value = true;
	error.value = "";

	try {
		const { data } = await postAction(action, payload);

		// The action answers 200 with success: false when it refuses the change
		if (!data?.success) {
			throw new Error(data?.error || fallback);
		}

		window.location.reload();
	} catch (e) {
		error.value = e.message;
		saving.value = false;
	}
}

async function save() {
	if (saving.value) {
		return;
	}

	await run(
		"site-module/dealer-reply/save",
		{
			reviewId: props.reviewId,
			replyId: props.replyId,
			text: text.value,
		},
		"Could not save your reply."
	);
}

async function remove() {
	if (saving.value) {
		return;
	}

	if (!window.confirm("Delete your reply? This cannot be undone.")) {
		return;
	}

	await run(
		"site-module/dealer-reply/delete",
		{ replyId: props.replyId },
		"Could not delete your reply."
	);
}
</script>
